import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FapIconComponent } from './fap-icon.component';

/**
 * Shared empty-state block for list modules (0.41.3).
 *
 * Instead of a bare `Keine Einträge.` the UI shows an icon, a
 * one-line message, and optionally a call-to-action. Two CTA flavours:
 *   - `ctaRouterLink` — Angular router navigation (relative or absolute array)
 *   - `ctaClick` event — caller-handled (e.g. open a modal, reset filters)
 *
 * Caller supplies translated strings via the `*Key` inputs; the
 * component does the `translate` pipe call itself so the template
 * stays clean on the consumer side.
 *
 * Three use-modes:
 *   (a) `filtered` — the filters just returned zero hits. Shows a
 *       muted icon + message. No CTA by default (the filter chips
 *       above already tell the user what to change).
 *   (b) `onboarding` — the resource has never been created. Shows
 *       a bigger icon + CTA button so the user knows what to do next.
 *   (c) `error` — the load failed, so the screen knows nothing about
 *       the resource. Shows a warning icon + message + a retry button
 *       that emits `retry`. Cut U3 (0.72.59) added it, because until
 *       then a failed load painted the `filtered` sentence and told the
 *       reader there was nothing rather than that nothing was fetched.
 *
 * `error` is additive: the retry button and the live region sit behind
 * `mode === 'error'`, so the shapes the other call sites use render as
 * before. `empty-state.component.spec.ts`
 * reads that out of the rendered DOM per mode, which is where a claim
 * about this component belongs; a sentence here would age unchecked.
 *
 * The two recert sites that build a failed state out of
 * `mode="onboarding"` plus `iconName="exclamation_triangle"` plus a
 * retry `ctaKey` keep that convention on purpose (cut U17 task 4
 * swept their emoji `icon` fallbacks to the Oblique sprite; cut U3
 * plan, "Not migrating the two recert sites"): they work, they are
 * pinned by a spec, and rewriting them buys nothing.
 */
@Component({
  selector: 'fap-empty-state',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, TranslateModule, FapIconComponent],
  template: `
    <div
      class="fap-empty"
      [class.fap-empty--onboarding]="mode === 'onboarding'"
      [class.fap-empty--error]="mode === 'error'">
      <div class="fap-empty__icon" aria-hidden="true">
        @if (iconName) {
          <fap-icon [name]="iconName" [size]="mode === 'onboarding' ? 48 : 32"></fap-icon>
        } @else {
          <fap-icon name="info_circle" [size]="mode === 'onboarding' ? 48 : 32"></fap-icon>
        }
      </div>
      <!-- Cut U3 review: the live region is the message, not the block. The
           block also holds the retry button, and a focusable control inside
           an announced region is re-announced on every re-render. The role
           is status rather than alert because a failed load is a state the
           reader stays in, and because every other standing message in the
           portal (the explore empty states, the loading lines this cut adds)
           announces the same way. -->
      <p
        class="fap-empty__msg"
        [attr.role]="mode === 'error' ? 'status' : null"
        [attr.aria-live]="mode === 'error' ? 'polite' : null">{{ messageKey | translate }}</p>
      @if (mode === 'error' && retryKey) {
        <button type="button" class="fap-btn fap-btn--primary fap-empty__retry" (click)="retry.emit()">
          {{ retryKey | translate }}
        </button>
      }
      @if (ctaKey && ctaRouterLink) {
        <a class="fap-btn fap-btn--primary" [routerLink]="ctaRouterLink">
          {{ ctaKey | translate }}
        </a>
      } @else if (ctaKey) {
        <button type="button" class="fap-btn fap-btn--primary" (click)="ctaClick.emit()">
          {{ ctaKey | translate }}
        </button>
      }
    </div>
  `,
  styles: [`
    .fap-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }
    .fap-empty__icon { font-size: 32px; opacity: 0.7; color: var(--ob-text-muted); }
    .fap-empty__icon fap-icon { color: var(--ob-text-muted); opacity: 0.6; }
    .fap-empty__msg  { margin: 0; font-size: 0.95rem; }
    .fap-empty--onboarding { padding: 48px 16px; }
    .fap-empty--onboarding .fap-empty__icon { font-size: 48px; opacity: 1; }
    .fap-empty--onboarding .fap-empty__icon fap-icon { color: var(--ob-primary); opacity: 0.85; }
    .fap-empty--onboarding .fap-empty__msg  { font-size: 1.05rem; color: var(--ob-text-dark); }
    .fap-empty--error .fap-empty__icon { color: var(--ob-error); opacity: 1; }
    .fap-empty--error .fap-empty__icon fap-icon { color: var(--ob-error); opacity: 1; }
    .fap-empty--error .fap-empty__msg  { color: var(--ob-error); }
    .fap-btn { margin-top: 8px; }
  `],
})
export class EmptyStateComponent {
  /** Translated message key, e.g. `i18n.fap.module.api_management.empty`. */
  @Input({ required: true }) messageKey!: string;
  /** Legacy glyph input, kept for backwards compatibility. Not rendered:
   *  the icon slot shows `iconName` when set, the static `info_circle`
   *  sprite otherwise. */
  @Input() icon?: string;
  /** Oblique-sprite icon name (preferred over `icon` since 0.57.38). */
  @Input() iconName?: string;
  /** When set, CTA button is rendered linking to this route. */
  @Input() ctaKey?: string;
  @Input() ctaRouterLink?: string | any[];
  /** Visual mode — compact for filtered-to-zero, bigger for truly-empty,
   *  warning for a load that failed. */
  @Input() mode: 'filtered' | 'onboarding' | 'error' = 'filtered';
  /** Label key of the retry button. Rendered only in `error` mode; without it
   *  the error state is a message with no way out, which is why every call
   *  site in cut U3 passes one. */
  @Input() retryKey?: string;
  /** Fires when the CTA button is clicked AND no `ctaRouterLink` is set.
   *  Use this for inline actions (open modal, toggle form) instead of navigation. */
  @Output() ctaClick = new EventEmitter<void>();
  /** Fires when the retry button is clicked. The caller re-runs its load. */
  @Output() retry = new EventEmitter<void>();
}
